import { useAIStore } from 'src/store/ai';
import { useAuthStore } from 'src/store/auth';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { useApplicationStore } from 'src/store/application';
import { useUnderReviewStore } from 'src/store/under-review';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { useGeneralStore } from 'src/store';
import {
  InsuranceChatRequest,
  InsuranceChatHook,
  ShowPlansParams,
  SelectPlansParams,
  DeselectPlansParams,
  ComparePlansParams,
  ShowPageParams,
  UpdateApplicantsParams,
  UpdateApplicantParams,
} from 'src/services/interfaces/ai';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { isActivePlan, withActivePricesOnly } from '@utils/planFilters';
import { format, parseISO } from 'date-fns';
import { useCompareStore } from 'src/store/compare';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useFormSteps } from '@features/onboarding/hooks/useFormSteps';
import {
  FormStepSections,
  FormStepID,
} from '@features/onboarding/enums/formSections.enum';
import { AI_HOOKS_IDS } from '@features/onboarding/constants/index';

export const useAI = () => {
  const { user } = useAuthStore();
  const plans = useInsuranceStore((state) => state.plans);
  const { data, setData, resetAllForms, setActiveDentalVisionCategory, currentStep, aiHighlightedPlanUid } =
    useOnboardingStore();
  const setAiHighlightedPlan = useOnboardingStore(
    (s) => s.setAiHighlightedPlan
  );
  const { handleJumpTo } = useFormControls();
  const navigate = useNavigate();
  const location = useLocation();
  const application = useApplicationStore((state) => state.applications?.[0]);
  const {
    // Estimation state
    estimationLoading,
    estimationResult,
    estimationError,

    // Chat state
    chatLoading,
    chatSessionId,
    chatHistory,
    chatError,

    // Actions
    runEstimation,
    sendChatMessage,
    generateNewSessionId,
    clearEstimation,
    clearChat,
    clearErrors,
  } = useAIStore();
  const applicationData = application ?? transformApplicationData(data, plans);
  const { open, setSelectedPlans, setCompareViewCategory, close, clearSelectedPlans } =
    useCompareStore();
  const formSteps = useFormSteps();

  // Helper functions
  const sendMessage = async (message: string) => {
    console.log('[useAI] sendMessage called', { message, chatSessionId, userId: user?.id });
    const chatData: InsuranceChatRequest = {
      message,
      session_id: chatSessionId || undefined, // Pass session_id if exists, otherwise let store generate it
    };
    if (user?.id) {
      chatData.userId = +user?.id;
    }

    const page_context =
      (
        document.getElementsByClassName(
          'w-full xl:pr-4 xl:pt-2.5 xl:pb-6 box-border rounded-2xl h-full bg-card'
        )?.[0] as HTMLElement
      )?.innerText || null;

    console.log('[useAI] Calling sendChatMessage', { chatData, hasApplicationData: !!applicationData, page_context });
    try {
      const result = await sendChatMessage(chatData, applicationData, page_context);
      console.log('[useAI] sendChatMessage completed', result);
      return result;
    } catch (error) {
      console.error('[useAI] sendChatMessage error:', error);
      throw error;
    }
  };

  const startNewChat = () => {
    generateNewSessionId();
    clearChat();
  };

  // Функция для обработки команды show_plans
  const handleShowPlans = (planIds: number[]) => {
    if (!planIds || planIds.length === 0) return;

    // Берем первый план из списка
    const planId = planIds[0];
    const plan = plans.find((p) => p.id === planId);

    if (!plan) {
      console.warn(`Plan with id ${planId} not found`);
      return;
    }

    // Определяем категорию плана и перенаправляем
    const category = plan.category?.toLowerCase();
    let sectionName = '';

    switch (category) {
      case 'major medical':
        sectionName = FormStepSections.Medical;
        break;
      case 'dental':
        sectionName = FormStepSections.Dental;
        break;
      case 'vision':
        sectionName = FormStepSections.Dental;
        break;
      case 'accident':
        sectionName = FormStepSections.Dental;
        break;
      case 'critical illness':
        sectionName = FormStepSections.Dental;
        break;
      case 'hospital indemnity':
        sectionName = FormStepSections.Dental;
        break;
      default:
        console.warn(`Unknown plan category: ${category}`);
    }

    // Проверяем, находимся ли мы уже на нужной странице с нужным планом
    const currentFormStep = formSteps[currentStep];
    const isAlreadyOnTargetSection = currentFormStep?.sectionName === sectionName;
    const isPlanAlreadyHighlighted = aiHighlightedPlanUid === plan.uid;

    // Если уже на нужной странице и план уже подсвечен, не делаем переход
    if (isAlreadyOnTargetSection && isPlanAlreadyHighlighted) {
      console.log('[useAI] Already on target section with highlighted plan, skipping navigation', {
        sectionName,
        planUid: plan.uid,
        currentSection: currentFormStep?.sectionName,
      });
      return;
    }

    setCompareViewCategory(null);
    clearSelectedPlans();
    close();

    if (category !== 'major medical') {
      // Используем ту же логику фильтрации, что и в handleShowPlansPage и DentalAndVisionForm
      const { siteConfig } = useGeneralStore.getState();
      const sitePlans = siteConfig?.plans;
      
      // Фильтруем планы так же, как в DentalAndVisionForm:
      // 1. Сначала убираем 'Major Medical'
      // 2. Затем применяем фильтр по sitePlans (если они есть)
      // 3. Затем убираем архивные планы и цены
      const extraPlans = plans.filter(
        (plan) => plan.category && plan.category !== 'Major Medical'
      );

      const availablePlansFiltered = (!sitePlans || sitePlans.length === 0
        ? extraPlans
        : extraPlans.filter((plan) => {
            const allowedIds = new Set(sitePlans);
            return allowedIds.has(plan.id);
          })
      ).filter(isActivePlan).map(withActivePricesOnly);

      // Проверяем, что запрошенный план доступен
      const isPlanAvailable = availablePlansFiltered.some((p) => p.id === planId);
      if (!isPlanAvailable) {
        console.warn('[useAI] handleShowPlans - Plan not available:', {
          planId,
          planCategory: plan.category,
          sitePlansLength: sitePlans?.length,
          availablePlansIds: availablePlansFiltered.map((p) => p.id),
          message: 'Plan is not available in siteConfig.plans. Cannot show plan.',
        });
        return; // Не показываем план, если он недоступен
      }

      // Получаем уникальные категории (используем ту же логику, что и в DentalAndVisionForm)
      const availableCategories = Array.from(
        new Set(
          availablePlansFiltered
            .map((p) => p.category!)
            .filter((category): category is string => !!category)
        )
      );

      // Используем ту же логику, что и в DentalAndVisionForm:
      // Dental всегда первый, если он есть, остальные в том же порядке
      const dentalExists = availableCategories.includes('Dental');
      const orderedCategories = dentalExists
        ? ['Dental', ...availableCategories.filter((c) => c !== 'Dental')]
        : availableCategories;

      const categoryIndex = orderedCategories.findIndex(
        (c) => c.toLowerCase() === category.toLowerCase()
      );

      console.log('[useAI] handleShowPlans - Setting active category:', {
        categoryIndex,
        orderedCategories,
        category,
        availableCategories,
        planId,
        planCategory: plan.category,
        sitePlansLength: sitePlans?.length,
        availablePlansCount: availablePlansFiltered.length,
        isPlanAvailable,
      });

      // Проверяем, что категория найдена и индекс в допустимых пределах
      if (categoryIndex === -1) {
        console.error('[useAI] handleShowPlans - Category not found in ordered categories:', {
          requestedCategory: category,
          orderedCategories,
          availableCategories,
          planId,
          planCategory: plan.category,
          message: 'Category is not available in siteConfig.plans. Cannot set active category.',
        });
        return; // Не выполняем навигацию, если категория недоступна
      } else if (categoryIndex >= 0 && categoryIndex < orderedCategories.length) {
        setActiveDentalVisionCategory(categoryIndex);
        console.log('[useAI] handleShowPlans - Set activeDentalVisionCategory to:', categoryIndex, 'for category:', orderedCategories[categoryIndex]);
        
        // Даем время для установки категории перед навигацией, чтобы DentalAndVisionForm не сбросил значение
        setTimeout(() => {
          const finalValue = useOnboardingStore.getState().activeDentalVisionCategory;
          console.log('[useAI] handleShowPlans - After delay, activeDentalVisionCategory is:', finalValue, 'expected:', categoryIndex);
          
          // Проверяем, что категория установлена правильно
          if (finalValue !== categoryIndex) {
            console.warn('[useAI] handleShowPlans - WARNING: activeDentalVisionCategory was changed from', categoryIndex, 'to', finalValue);
            // Пытаемся установить снова, но только если новый индекс в допустимых пределах
            if (finalValue >= 0 && finalValue < orderedCategories.length) {
              console.warn('[useAI] handleShowPlans - Category was changed to valid index, but different from requested. Requested:', categoryIndex, 'Actual:', finalValue);
              // Проверяем, соответствует ли установленная категория запрошенной
              const actualCategory = orderedCategories[finalValue];
              if (actualCategory?.toLowerCase() !== category.toLowerCase()) {
                console.error('[useAI] handleShowPlans - ERROR: Category mismatch! Requested:', category, 'Actual:', actualCategory);
                // Пытаемся установить правильный индекс еще раз
                setActiveDentalVisionCategory(categoryIndex);
              }
            } else {
              // Если индекс вышел за границы, устанавливаем правильный
              setActiveDentalVisionCategory(categoryIndex);
            }
          }
          
          // Перенаправляем на нужную страницу только если мы не на ней уже
          if (!isAlreadyOnTargetSection) {
            handleJumpTo(sectionName);

            if (location.pathname !== '/form') {
              navigate('/form');
            }
          }
        }, 100); // Даем время для установки категории
        
        return; // Выходим из функции, навигация произойдет в setTimeout
      } else {
        console.error('[useAI] handleShowPlans - Invalid category index:', {
          categoryIndex,
          orderedCategoriesLength: orderedCategories.length,
          orderedCategories,
          requestedCategory: category,
        });
        return; // Не выполняем навигацию при невалидном индексе
      }
    }

    // Для Major Medical планов навигация происходит сразу (без задержки)
    // Перенаправляем на нужную страницу только если мы не на ней уже
    if (!isAlreadyOnTargetSection) {
      handleJumpTo(sectionName);

      if (location.pathname !== '/form') {
        navigate('/form');
      }
    }
  };

  const handleSelectPlans = async (planIds: number[]) => {
    if (!planIds || planIds.length === 0) return;

    const planId = planIds[0];
    const plan = plans.find((p) => p.id === planId);
    if (!plan) {
      console.warn(`Plan with id ${planId} not found`);
      return;
    }

    // Re-fetch active subscriptions to ensure we have the latest state
    await useApplicationStore.getState().fetchActiveSubscriptionPlans();

    const { activeCategories, signedAt, hasCompletedApplication } = useApplicationStore.getState();
    if (signedAt !== null && signedAt !== undefined && !hasCompletedApplication) {
      console.warn('Plan selection via chat is disabled: contract is signed but payment is not completed');
      return;
    }
    const category = plan.category?.toLowerCase();

    if (category === 'major medical') {
      const { isMainPlanRejected } = useApplicationStore.getState();
      if (isMainPlanRejected) {
        console.warn(`Medical plan category is blocked (rejected)`);
        return;
      }
      if (activeCategories.includes('Major Medical')) {
        console.warn(`Medical plan category is blocked (already active)`);
        return;
      }
    } else if (plan.category) {
      const hasAnyActiveExtraPlan = activeCategories.some(
        (cat) => cat !== 'Major Medical'
      );
      if (hasAnyActiveExtraPlan) {
        console.warn(
          `Extra plan selection is blocked: an active extra plan already exists`
        );
        return;
      }
    }
    const sanitizeCategory = (cat: string) =>
      cat.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

    let fieldName = '';
    let sectionName = '';

    if (category === 'major medical') {
      fieldName = 'medicalPlan';
      sectionName = FormStepSections.Medical;
    } else if (category === 'dental') {
      fieldName = 'dentalPlan';
      sectionName = FormStepSections.Dental;
    } else if (category === 'vision') {
      fieldName = 'visionPlan';
      sectionName = FormStepSections.Dental;
    } else {
      fieldName = `${sanitizeCategory(plan.category || '')}Plan`;
      sectionName = FormStepSections.Dental;
    }

    if (category !== 'major medical') {
      // Используем ту же логику фильтрации, что и в handleShowPlansPage и DentalAndVisionForm
      const { siteConfig } = useGeneralStore.getState();
      const sitePlans = siteConfig?.plans;
      
      // Фильтруем планы так же, как в DentalAndVisionForm:
      // 1. Сначала убираем 'Major Medical'
      // 2. Затем применяем фильтр по sitePlans (если они есть)
      // 3. Затем убираем архивные планы и цены
      const extraPlans = plans.filter(
        (plan) => plan.category && plan.category !== 'Major Medical'
      );

      const availablePlansFiltered = (!sitePlans || sitePlans.length === 0
        ? extraPlans
        : extraPlans.filter((plan) => {
            const allowedIds = new Set(sitePlans);
            return allowedIds.has(plan.id);
          })
      ).filter(isActivePlan).map(withActivePricesOnly);

      // Проверяем, что запрошенный план доступен
      const isPlanAvailable = availablePlansFiltered.some((p) => p.id === planId);
      if (!isPlanAvailable) {
        console.warn('[useAI] handleSelectPlans - Plan not available:', {
          planId,
          planCategory: plan.category,
          sitePlansLength: sitePlans?.length,
          availablePlansIds: availablePlansFiltered.map((p) => p.id),
          message: 'Plan is not available in siteConfig.plans. Cannot select plan.',
        });
        return; // Не выбираем план, если он недоступен
      }

      // Получаем уникальные категории (используем ту же логику, что и в DentalAndVisionForm)
      const availableCategories = Array.from(
        new Set(
          availablePlansFiltered
            .map((p) => p.category!)
            .filter((category): category is string => !!category)
        )
      );

      // Используем ту же логику, что и в DentalAndVisionForm:
      // Dental всегда первый, если он есть, остальные в том же порядке
      const dentalExists = availableCategories.includes('Dental');
      const orderedCategories = dentalExists
        ? ['Dental', ...availableCategories.filter((c) => c !== 'Dental')]
        : availableCategories;

      const categoryIndex = orderedCategories.findIndex(
        (c) => c.toLowerCase() === category.toLowerCase()
      );

      console.log('[useAI] handleSelectPlans - Setting active category:', {
        categoryIndex,
        orderedCategories,
        category,
        availableCategories,
        planId,
        planCategory: plan.category,
        sitePlansLength: sitePlans?.length,
        availablePlansCount: availablePlansFiltered.length,
        isPlanAvailable,
      });

      // Проверяем, что категория найдена и индекс в допустимых пределах
      if (categoryIndex === -1) {
        console.error('[useAI] handleSelectPlans - Category not found in ordered categories:', {
          requestedCategory: category,
          orderedCategories,
          availableCategories,
          planId,
          planCategory: plan.category,
          message: 'Category is not available in siteConfig.plans. Cannot set active category.',
        });
        return; // Не выполняем выбор плана, если категория недоступна
      } else if (categoryIndex >= 0 && categoryIndex < orderedCategories.length) {
        setActiveDentalVisionCategory(categoryIndex);
        console.log('[useAI] handleSelectPlans - Set activeDentalVisionCategory to:', categoryIndex, 'for category:', orderedCategories[categoryIndex]);
        
        // Даем время для установки категории перед навигацией, чтобы DentalAndVisionForm не сбросил значение
        setTimeout(() => {
          const finalValue = useOnboardingStore.getState().activeDentalVisionCategory;
          console.log('[useAI] handleSelectPlans - After delay, activeDentalVisionCategory is:', finalValue, 'expected:', categoryIndex);
          
          // Проверяем, что категория установлена правильно
          if (finalValue !== categoryIndex) {
            console.warn('[useAI] handleSelectPlans - WARNING: activeDentalVisionCategory was changed from', categoryIndex, 'to', finalValue);
            // Пытаемся установить снова, но только если новый индекс в допустимых пределах
            if (finalValue >= 0 && finalValue < orderedCategories.length) {
              console.warn('[useAI] handleSelectPlans - Category was changed to valid index, but different from requested. Requested:', categoryIndex, 'Actual:', finalValue);
              // Проверяем, соответствует ли установленная категория запрошенной
              const actualCategory = orderedCategories[finalValue];
              if (actualCategory?.toLowerCase() !== category.toLowerCase()) {
                console.error('[useAI] handleSelectPlans - ERROR: Category mismatch! Requested:', category, 'Actual:', actualCategory);
                // Пытаемся установить правильный индекс еще раз
                setActiveDentalVisionCategory(categoryIndex);
              }
            } else {
              // Если индекс вышел за границы, устанавливаем правильный
              setActiveDentalVisionCategory(categoryIndex);
            }
          }
          
          // Подсветим план в UI (только если все проверки пройдены)
          setAiHighlightedPlan(plan.uid);

          setData({ [fieldName]: plan.uid } as any);

          handleJumpTo(sectionName);

          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('ai-select-plan', {
                detail: { planUid: plan.uid, fieldName },
              })
            );
          }, 1000);
        }, 100); // Даем время для установки категории
        
        return; // Выходим из функции, навигация произойдет в setTimeout
      } else {
        console.error('[useAI] handleSelectPlans - Invalid category index:', {
          categoryIndex,
          orderedCategoriesLength: orderedCategories.length,
          orderedCategories,
          requestedCategory: category,
        });
        return; // Не выполняем выбор плана при невалидном индексе
      }
    }

    // Для Major Medical планов навигация происходит сразу (без задержки)
    // Подсветим план в UI
    setAiHighlightedPlan(plan.uid);

    setData({ [fieldName]: plan.uid } as any);

    handleJumpTo(sectionName);

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('ai-select-plan', {
          detail: { planUid: plan.uid, fieldName },
        })
      );
    }, 1000);
  };

  const handleDeselectPlans = (planIds: number[]) => {
    if (!planIds || planIds.length === 0) return;

    const planId = planIds[0];
    const plan = plans.find((p) => p.id === planId);
    if (!plan) {
      console.warn(`Plan with id ${planId} not found`);
      return;
    }

    const { signedAt, hasCompletedApplication } = useApplicationStore.getState();
    if (signedAt !== null && signedAt !== undefined && !hasCompletedApplication) {
      console.warn('Plan deselection via chat is disabled: contract is signed but payment is not completed');
      return;
    }

    const sanitizeCategory = (cat: string) =>
      cat.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

    const category = plan.category?.toLowerCase();
    let fieldName = '';

    if (category === 'major medical') {
      fieldName = 'medicalPlan';
    } else if (category === 'dental') {
      fieldName = 'dentalPlan';
    } else if (category === 'vision') {
      fieldName = 'visionPlan';
    } else {
      fieldName = `${sanitizeCategory(plan.category || '')}Plan`;
    }

    const baseKey = fieldName.replace('Plan', '');
    
    setData({
      [fieldName]: '',
      [`${baseKey}PlansToCompare`]: [],
      [`${baseKey}PlanPrice`]: 0,
      [`${baseKey}PlanTier`]: '',
      [`${baseKey}PlanCoverageAmount`]: plan.category === 'Critical Illness' ? 10000 : null,
    } as any);
    
    setAiHighlightedPlan(null);

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('ai-deselect-plan', {
          detail: { planUid: plan.uid, fieldName },
        })
      );
    }, 100);
  };

  // Функция для обработки команды compare_plans
  const handleComparePlans = (planIds: number[]) => {
    if (!planIds || planIds.length < 2) {
      console.warn('Need at least 2 plans to compare');
      return;
    }

    // Находим планы по ID
    const plansToCompare = planIds
      .map((id) => plans.find((p) => p.id === id))
      .filter((p): p is InsurancePlan => !!p);

    if (plansToCompare.length !== planIds.length) {
      console.warn('Some plans not found for comparison');
      return;
    }

    // TODO
    // Определяем категорию по первому плану
    const firstPlan = plansToCompare[0];
    const category = firstPlan.category?.toLowerCase();
    console.log('firstPlan', category, firstPlan);
    let sectionName = '';
    let compareCategory = '';

    switch (category) {
      case 'major medical':
        sectionName = FormStepSections.Medical;
        compareCategory = 'Major Medical';
        break;
      case 'dental':
        sectionName = FormStepSections.Dental;
        compareCategory = 'Dental';
        break;
      case 'vision':
        sectionName = FormStepSections.Dental;
        compareCategory = 'Vision';
        break;
      case 'accident':
        sectionName = FormStepSections.Dental;
        compareCategory = 'Accident';
        break;
      case 'critical illness':
        sectionName = FormStepSections.Dental;
        compareCategory = 'Critical Illness';
        break;
      case 'hospital indemnity':
        sectionName = FormStepSections.Dental;
        compareCategory = 'Hospital Indemnity';
        break;
      default:
        console.warn(`Unknown plan category: ${category}`);
        return;
    }

    // Проверяем, что все планы принадлежат одной категории
    const categories = [
      ...new Set(plansToCompare.map((p) => p.category?.toLowerCase())),
    ];
    if (categories.length > 1) {
      console.warn('Cannot compare plans from different categories');
      return;
    }

    // Устанавливаем планы для сравнения
    setSelectedPlans(plansToCompare.map((p) => p.uid));
    // Устанавливаем категорию для сравнения
    setCompareViewCategory(compareCategory);
    // Открываем модальное окно сравнения
    open();

    if (category !== 'major medical') {
      // Получаем все доступные категории планов (кроме Major Medical)
      const availableCategories = plans
        .filter((plan) => plan.category && plan.category !== 'Major Medical')
        .map((plan) => plan.category!)
        .filter((category, index, arr) => arr.indexOf(category) === index); // убираем дубликаты

      // Ставим Dental на первое место, если он есть
      const dentalExists = availableCategories.includes('Dental');
      const orderedCategories = dentalExists
        ? ['Dental', ...availableCategories.filter((c) => c !== 'Dental')]
        : availableCategories;

      // Находим индекс категории
      const categoryIndex = orderedCategories.findIndex(
        (cat) => cat.toLowerCase() === category
      );

      if (categoryIndex !== -1) {
        setActiveDentalVisionCategory(categoryIndex);
      }
    }

    // Перенаправляем на нужную страницу
    handleJumpTo(sectionName);
    console.log(
      `Redirecting to ${sectionName} for plan comparison:`,
      plansToCompare
    );
  };

  // Функция для обработки команды update_applicants
  const handleUpdateApplicants = (applicantsData: UpdateApplicantParams[]) => {
    if (!applicantsData || applicantsData.length === 0) {
      console.warn('No applicant data provided');
      return;
    }

    // Получаем текущих заявителей
    const currentApplicants = data.applicants || [];
    const updatedApplicants = [...currentApplicants];

    // Обновляем каждого заявителя
    applicantsData.forEach((applicantUpdate) => {
      const { applicant: applicantIndex, ...updateFields } = applicantUpdate;

      if (applicantIndex < 0 || applicantIndex >= updatedApplicants.length) {
        console.warn(`Invalid applicant index: ${applicantIndex}`);
        return;
      }

      const currentApplicant = updatedApplicants[applicantIndex];

      // Обновляем только не-null поля
      Object.entries(updateFields).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          // Обрабатываем специальные поля
          if (key === 'dob' && typeof value === 'string') {
            // Преобразуем дату в формат, используемый в приложении
            const date = parseISO(format(new Date(value), 'yyyy-MM-dd'));
            currentApplicant.dateOfBirth = {
              month: (date.getMonth() + 1).toString().padStart(2, '0'),
              day: date.getDate().toString().padStart(2, '0'),
              year: date.getFullYear().toString(),
            } as any;
          } else if (key === 'heightFt' || key === 'heightIn') {
            // Обновляем рост
            if (!currentApplicant.height) {
              currentApplicant.height = { ft: 0, in: 0 } as any;
            }
            if (key === 'heightFt') {
              (currentApplicant.height as any).ft = value as number;
            } else if (key === 'heightIn') {
              (currentApplicant.height as any)['in'] = value as number;
            }
          } else {
            // Обновляем остальные поля напрямую
            (currentApplicant as any)[key] = value;
          }
        }
      });
    });

    // Обновляем данные в store
    const updatedData = {
      ...data,
      applicants: updatedApplicants,
    };

    setData(updatedData);

    // Обновляем все зарегистрированные формы
    resetAllForms(updatedData);

    console.log('Applicants updated:', updatedApplicants);
  };

  const isEstimationAccepted = estimationResult?.status === 'accepted';
  const isEstimationRejected = estimationResult?.status === 'rejected';

  const handleShowPlansPage = (pageId: string) => {
    const pageIdToCategory: Record<string, string> = {
      [AI_HOOKS_IDS.DENTAL_PLANS]: 'dental',
      [AI_HOOKS_IDS.VISION_PLANS]: 'vision',
      [AI_HOOKS_IDS.ACCIDENT_PLANS]: 'accident',
      [AI_HOOKS_IDS.CRITICAL_ILLNESS_PLANS]: 'critical illness',
      [AI_HOOKS_IDS.HOSPITAL_INDEMNITY_PLANS]: 'hospital indemnity',
    };

    const category = pageIdToCategory[pageId];
    console.log('[useAI] handleShowPlansPage called:', {
      pageId,
      category,
      currentPath: location.pathname,
    });

    if (category) {
      setCompareViewCategory(null);
      clearSelectedPlans();
      close();

      // Используем ту же логику, что и в DentalAndVisionForm для определения категорий
      const { siteConfig } = useGeneralStore.getState();
      const sitePlans = siteConfig?.plans;
      
      // Фильтруем планы так же, как в DentalAndVisionForm:
      // 1. Сначала убираем 'Major Medical'
      // 2. Затем применяем фильтр по sitePlans (если они есть)
      // 3. Затем убираем архивные планы и цены
      const extraPlans = plans.filter(
        (plan) => plan.category && plan.category !== 'Major Medical'
      );

      const availablePlansFiltered = (!sitePlans || sitePlans.length === 0
        ? extraPlans
        : extraPlans.filter((plan) => {
            const allowedIds = new Set(sitePlans);
            return allowedIds.has(plan.id);
          })
      ).filter(isActivePlan).map(withActivePricesOnly);

      // Получаем уникальные категории (используем ту же логику, что и в DentalAndVisionForm)
      const availableCategories = Array.from(
        new Set(
          availablePlansFiltered
            .map((p) => p.category!)
            .filter((category): category is string => !!category)
        )
      );

      // Используем ту же логику, что и в DentalAndVisionForm:
      // Dental всегда первый, если он есть, остальные в том же порядке
      const dentalExists = availableCategories.includes('Dental');
      const orderedCategories = dentalExists
        ? ['Dental', ...availableCategories.filter((c) => c !== 'Dental')]
        : availableCategories;

      const categoryIndex = orderedCategories.findIndex(
        (c) => c.toLowerCase() === category.toLowerCase()
      );

      console.log('[useAI] Setting active category:', {
        categoryIndex,
        orderedCategories,
        category,
        availableCategories,
        sitePlansLength: sitePlans?.length,
        availablePlansCount: availablePlansFiltered.length,
        currentActiveDentalVisionCategory: useOnboardingStore.getState().activeDentalVisionCategory,
      });

      // Если категория не найдена в доступных планах, не выполняем навигацию
      if (categoryIndex === -1) {
        console.warn('[useAI] Category not available:', {
          requestedCategory: category,
          availableCategories: orderedCategories,
          message: 'Category is not available in siteConfig.plans. Staying on current page.',
        });
        return true; // Возвращаем true, но не выполняем навигацию
      }

      // Проверяем, что индекс находится в допустимых пределах
      if (categoryIndex < 0 || categoryIndex >= orderedCategories.length) {
        console.error('[useAI] Invalid category index:', {
          categoryIndex,
          orderedCategoriesLength: orderedCategories.length,
          orderedCategories,
          requestedCategory: category,
          message: 'Category index is out of bounds. Staying on current page.',
        });
        return true; // Возвращаем true, но не выполняем навигацию
      }

      const targetCategory = orderedCategories[categoryIndex];
      console.log('[useAI] Calling setActiveDentalVisionCategory with index:', categoryIndex, 'for category:', targetCategory);
      
      // Устанавливаем категорию СИНХРОННО перед handleJumpTo
      setActiveDentalVisionCategory(categoryIndex);
      const newValue = useOnboardingStore.getState().activeDentalVisionCategory;
      console.log('[useAI] Immediately after setActiveDentalVisionCategory, value is:', newValue, 'expected:', categoryIndex);
      
      // Даем время для установки категории перед переходом
      setTimeout(() => {
        const finalValue = useOnboardingStore.getState().activeDentalVisionCategory;
        console.log('[useAI] After 50ms, activeDentalVisionCategory is:', finalValue, 'expected:', categoryIndex);
        
        // Проверяем, что категория установлена правильно
        if (finalValue !== categoryIndex) {
          console.warn('[useAI] WARNING: activeDentalVisionCategory was changed from', categoryIndex, 'to', finalValue);
          // Пытаемся установить снова, но только если новый индекс в допустимых пределах
          if (finalValue >= 0 && finalValue < orderedCategories.length) {
            console.warn('[useAI] Category was changed to valid index, but different from requested. Requested:', categoryIndex, 'Actual:', finalValue);
            // Проверяем, соответствует ли установленная категория запрошенной
            const actualCategory = orderedCategories[finalValue];
            if (actualCategory?.toLowerCase() !== category.toLowerCase()) {
              console.error('[useAI] ERROR: Category mismatch! Requested:', category, 'Actual:', actualCategory);
              // Пытаемся установить правильный индекс еще раз
              setActiveDentalVisionCategory(categoryIndex);
            }
          } else {
            // Если индекс вышел за границы, устанавливаем правильный
            setActiveDentalVisionCategory(categoryIndex);
          }
        }
        
        handleJumpTo(FormStepSections.Dental);

        if (location.pathname !== '/form') {
          navigate('/form');
        }
      }, 50);

      return true;
    }

    return false;
  };

  // Обработка хуков из AI ответов
  useEffect(() => {
    const handleAIHooks = async (event: CustomEvent) => {
      const { hooks } = event.detail;

      for (const hook of hooks) {
        switch (hook.tool) {
          case 'show_page': {
            const showPageParams = hook.params as ShowPageParams;
            const pageId = showPageParams?.page;
            console.log('[useAI] show_page hook triggered:', {
              pageId,
              currentPath: location.pathname,
              aiHighlightedPlanUid: useOnboardingStore.getState().aiHighlightedPlanUid,
            });
            if (pageId) {
              const { sessionLockedForHumanReview } = useUnderReviewStore.getState();
              if (sessionLockedForHumanReview) {
                console.warn(
                  '[useAI] show_page blocked: session is locked for human review, page navigation disabled until refresh',
                  { pageId }
                );
                break;
              }

              await useApplicationStore.getState().updateTierFromApplicationState();
              const { phqUnderReviewAt } = useApplicationStore.getState();
              const isUnderReview = !!phqUnderReviewAt;

              if (isUnderReview) {
                console.warn(
                  '[useAI] show_page blocked: application is under review',
                  { pageId }
                );
                break;
              }

              // Сбрасываем подсветку плана, чтобы предотвратить автоматическое раскрытие
              setAiHighlightedPlan(null);
              console.log('[useAI] Reset aiHighlightedPlanUid to null');
              
              if (!handleShowPlansPage(pageId)) {
                // Match exact step id first, then try prefix match for
                // composite ids like "Finish-payment"
                const targetStep =
                  formSteps.find((step) => step.id === pageId) ||
                  formSteps.find((step) => pageId.startsWith(step.id + '-'));
                console.log('[useAI] Target step found:', {
                  targetStep: targetStep?.id,
                  sectionName: targetStep?.sectionName,
                });
                if (targetStep) {
                  // For composite IDs like "Finish-payment", extract the subStep
                  if (targetStep.id === 'Finish' && pageId.startsWith('Finish-')) {
                    const subStep = pageId.slice('Finish-'.length);
                    if (
                      subStep === 'quote' ||
                      subStep === 'address' ||
                      subStep === 'contract' ||
                      subStep === 'payment'
                    ) {
                      useOnboardingStore.getState().setFinishSubStep(subStep);
                    }
                  }
                  handleJumpTo(targetStep.sectionName);
                  if (location.pathname !== '/form') {
                    navigate('/form');
                  }
                }
              } else {
                console.log('[useAI] handleShowPlansPage returned true');
              }
            }
            break;
          }
          case 'show_plans':
            const showPlansParams = hook.params as ShowPlansParams;
            if (
              showPlansParams?.plans &&
              Array.isArray(showPlansParams.plans)
            ) {
              handleShowPlans(showPlansParams.plans);
            }
            break;
          case 'select_plans':
            const selectPlansParams = hook.params as SelectPlansParams;
            if (
              selectPlansParams?.plans &&
              Array.isArray(selectPlansParams.plans)
            ) {
              handleSelectPlans(selectPlansParams.plans);
            }
            break;
          case 'deselect_plans':
            const deselectPlansParams = hook.params as DeselectPlansParams;
            if (
              deselectPlansParams?.plans &&
              Array.isArray(deselectPlansParams.plans)
            ) {
              handleDeselectPlans(deselectPlansParams.plans);
            }
            break;
          case 'compare_plans':
            const comparePlansParams = hook.params as ComparePlansParams;
            if (
              comparePlansParams?.plans &&
              Array.isArray(comparePlansParams.plans)
            ) {
              handleComparePlans(comparePlansParams.plans);
            }
            break;
          case 'update_applicants':
            const updateApplicantsParams =
              hook.params as UpdateApplicantsParams;
            if (
              updateApplicantsParams?.applicants &&
              Array.isArray(updateApplicantsParams.applicants)
            ) {
              handleUpdateApplicants(
                updateApplicantsParams.applicants as UpdateApplicantParams[]
              );
            }
            break;
          default:
            console.warn('Unknown hook type:', hook.tool);
        }
      }
    };

    // Слушаем события хуков
    window.addEventListener('ai-hooks', handleAIHooks as EventListener);

    return () => {
      window.removeEventListener('ai-hooks', handleAIHooks as EventListener);
    };
  }, [
    plans,
    handleJumpTo,
    data,
    setData,
    setActiveDentalVisionCategory,
    formSteps,
    location.pathname,
    navigate,
    currentStep,
    aiHighlightedPlanUid,
    setAiHighlightedPlan,
  ]);

  return {
    // Estimation
    estimationLoading,
    estimationResult,
    estimationError,
    isEstimationAccepted,
    isEstimationRejected,
    runEstimation,
    clearEstimation,

    // Chat
    chatLoading,
    chatSessionId,
    chatHistory,
    chatError,
    sendMessage,
    startNewChat,
    generateNewSessionId,
    clearChat,

    // Plan navigation
    handleShowPlans,
    handleSelectPlans,
    handleDeselectPlans,
    handleComparePlans,

    // Applicant management
    handleUpdateApplicants,

    // General
    clearErrors,
  };
};
